import Button from "app/storybookComponents/Button";
import { Card, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { useEffect } from "react";
import {
  selectChatbotMessages,
  selectThreadId,
  resetChatbotState,
  selectSelectedChatbotPrompt,
  setChatbotMessages,
  selectChatbotFullScreen,
  setChatbotFullScreen,
  selectIsThreadIdWeeklyCheckinMap,
  selectSampleChatState,
  updateUserCoachBoLanguage,
  selectAvailableCoachBoLanguages,
  getCoachBoAvailableLanguages,
} from "./slice";

import { WEEKLY_CHECK_IN_FEATURE_NAME } from "../WeeklyCheckInMonitor/constants";
import ChatbotContent from "./ChatbotContent";
import { selectCurrentUserInfo } from "../Global/slice";
import { selectCompanySettings } from "../AdminConsole/slice";
import SearchableDropdownAlt from "app/storybookComponents/SearchableInput/SearchableDropdownAlt";

interface Props {
  readonly isOpen: boolean;
  readonly closeChatbot: () => void;
  readonly isLoginless?: boolean;
}

export default function Chatbot({
  isOpen,
  closeChatbot,
  isLoginless = false,
}: Props) {
  const dispatch = useAppDispatch();
  // ---------------------------------------- Selectors ----------------------------------------
  const messages = useAppSelector(selectChatbotMessages);
  const currentUserInfo = useAppSelector(selectCurrentUserInfo);
  const companySettings = useAppSelector(selectCompanySettings);
  const threadId = useAppSelector(selectThreadId);
  const selectedCoachbotPrompt = useAppSelector(selectSelectedChatbotPrompt);
  const isFullScreen = useAppSelector(selectChatbotFullScreen);
  const isThreadIdWeeklyCheckinMap = useAppSelector(
    selectIsThreadIdWeeklyCheckinMap
  );
  const availableCoachBoLanguages = useAppSelector(
    selectAvailableCoachBoLanguages
  );
  const sampleChatState = useAppSelector(selectSampleChatState);

  // ---------------------------------------- Effects ----------------------------------------
  useEffect(() => {
    // Force full screen mode when in loginless mode
    if (isLoginless && !isFullScreen) {
      dispatch(setChatbotFullScreen(true));
    }
  }, [isLoginless, isFullScreen, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    // When the chatbot opens, get the available languages if not already fetched
    dispatch(getCoachBoAvailableLanguages());
  }, [dispatch, isOpen]);

  // ---------------------------------------- Refs ----------------------------------------
  if (!isOpen) return null;

  const setIsFullScreen = (value: boolean) => {
    // Don't allow disabling full screen in loginless mode
    if (isLoginless && !value) return;
    dispatch(setChatbotFullScreen(value));
  };

  const closeButtonWrapper = () => {
    closeChatbot();
    setIsFullScreen(false);
  };

  const isWeeklyCheckinChat =
    isLoginless ||
    !!(threadId && isThreadIdWeeklyCheckinMap[threadId]) ||
    (sampleChatState.isSampleChatShowing &&
      sampleChatState.isCheckingSampleChat);

  const onDropdownSelect = (e: string | null) => {
    if (e !== "reset") {
      return;
    }

    const firstMessage = messages[0];
    dispatch(
      resetChatbotState({
        keepPrompt: true,
        keepChatOpen: true,
      })
    );
    dispatch(setChatbotMessages([firstMessage]));
  };

  const getCoachBoLanguageSetting = () => {
    const selectedLanguage =
      currentUserInfo?.coachBoLanguage ??
      companySettings?.coachBoLanguage ??
      "English";

    const options = availableCoachBoLanguages.map((language) => ({
      value: language,
      label: language,
    }));

    return (
      <div className="setting-row px-3 py-2">
        <p>
          <b>Language</b>
        </p>

        <SearchableDropdownAlt
          options={options}
          onSelect={(language) => dispatch(updateUserCoachBoLanguage(language))}
          selectedValue={selectedLanguage}
        />
      </div>
    );
  };

  const getDropdown = () => {
    if (isLoginless || selectedCoachbotPrompt?.chatType === "savedChat") {
      return null;
    }
    return (
      <Dropdown onSelect={onDropdownSelect} style={{ paddingRight: "10px" }}>
        <Dropdown.Toggle
          variant="outline-primary"
          id="dropdown-basic"
          className="no-caret"
        >
          <FontAwesomeIcon icon="ellipsis" size="sm" />
        </Dropdown.Toggle>

        <Dropdown.Menu className="flip">
          <Dropdown.Item
            eventKey="reset"
            disabled={!threadId || messages.length <= 1}
          >
            Reset Chat History
          </Dropdown.Item>
          <Dropdown.Divider />
          {getCoachBoLanguageSetting()}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const getCoachBoTitle = () => {
    if (isWeeklyCheckinChat) {
      // For loginless mode, show just the feature name
      if (isLoginless) {
        return "Manager Check-in";
      }
      return `${WEEKLY_CHECK_IN_FEATURE_NAME} (Shared)`;
    }
    return "Coach Bo Chat (Private)";
  };

  const shouldShowFullScreen = isFullScreen || isLoginless;

  return (
    <>
      {shouldShowFullScreen && (
        <div
          className="backdrop"
          onClick={() => setIsFullScreen(false)}
          role="button"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setIsFullScreen(false);
            }
          }}
        />
      )}
      <div className={shouldShowFullScreen ? "full-screen-wrapper" : ""}>
        <Card className="chatbot-card">
          <div className="chatbot-header">
            <h2>
              {getCoachBoTitle()}
              {!isLoginless && (
                <FontAwesomeIcon
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  icon={
                    shouldShowFullScreen
                      ? "down-left-and-up-right-to-center"
                      : "up-right-and-down-left-from-center"
                  }
                  className="chatbot-fullscreen-toggle"
                />
              )}
            </h2>
            <div style={{ display: "flex", alignItems: "center" }}>
              {getDropdown()}

              {!isLoginless && (
                <Button
                  xIcon
                  variant="secondary-blue"
                  className="border-0"
                  onClick={() => {
                    closeButtonWrapper();
                    setIsFullScreen(false);
                  }}
                />
              )}
            </div>
          </div>
          <ChatbotContent
            closeChatbot={closeChatbot}
            isLoginless={isLoginless}
          />
        </Card>
      </div>
    </>
  );
}
